import { Injectable } from '@angular/core';
import { RankingResponse } from './models/ranking.response';
import { RankingByScope, RankingType } from './models/ranking.model';
import { BadgeCategory, CategoryBadges, LeesBadgeNotToShow, RankingBadge } from './models/badges.model';
import { ConfigService } from 'src/app/services/config.service';
import { BadgeResponse } from './models/badges.response';
import { LocaleService } from 'src/app/services/locale.service';
import { ScoreDetailResponse } from './models/score-detail.response';
import { Detail, ScoreDetail } from './models/score-detail.model';

@Injectable({
  providedIn: 'root',
})
export class GamificationFactory {
  constructor(
    private readonly configService: ConfigService,
    private readonly localeService: LocaleService
  ) {}

  buildRanking(ranking: RankingResponse, type: RankingType): RankingByScope {
    const scope: RankingByScope = this.getScope(ranking, type);

    const centerRanking = scope['center'];
    const platformRanking = scope['platform'];

    if (centerRanking?.slots) {
      centerRanking.slots = centerRanking?.slots.map((slot) => {
        return {
          ...slot,
          userName: ranking.usersMap[slot.user],
        };
      });
    }

    if (platformRanking?.slots) {
      platformRanking.slots = platformRanking?.slots.map((slot) => {
        return {
          ...slot,
          userName: ranking.usersMap[slot.user],
        };
      });
    }

    return {
      center: centerRanking ?? null,
      platform: platformRanking ?? null,
    };
  }

  buildBadges(badges: BadgeResponse[]): CategoryBadges[] {
    const categories = [...new Set(badges.map((badge) => badge.category))];

    return categories.map((category) => {
      const categoryTitle = this.getCategoryTitle(category);
      const filteredBadges = badges
        .filter((badge) => {
          if (!this.isBadgeToClientLees(badge) && this.shouldShowBadge(badge)) {
            return badge.category === category;
          }
          return null;
        })
        .map((badge) => ({
          ...badge,
          timesWonLabel: this.getTimesWonLabel(badge.timesWon),
        }));

      return {
        categoryTitle,
        badges: filteredBadges,
      };
    });
  }

  buildScore(score: ScoreDetailResponse): ScoreDetail {
    if (score) {
      const actions: Detail[] = Object.keys(score).map((key) => ({
        action: this.getTranslationKey(key),
        tooltip: this.getTooltipText(key),
        value: score[key]?.value ?? 0,
        amount: score[key]?.amount ?? 0,
        subtotal: score[key]?.subtotal ?? 0,
      }));

      return { actions };
    }
    return null;
  }

  private getScope(ranking: RankingResponse, type: RankingType): RankingByScope {
    if (type === RankingType.MONTHLY) {
      return {
        center: ranking.centerMonth,
        platform: ranking.platformMonth,
      };
    }
    return {
      center: ranking.centerAlltime,
      platform: ranking.platformAlltime,
    };
  }

  private getCategoryTitle(category: BadgeCategory): string {
    switch (category) {
      case BadgeCategory.FIRST_STEPS:
        return 'user.badges.categories.firstSteps';
      case BadgeCategory.MAKING_PROGRESS:
        return 'user.badges.categories.makingProgress';
      case BadgeCategory.CRONO:
        return 'user.badges.categories.crono';
      case BadgeCategory.SOCIAL:
        return 'user.badges.categories.social';
      case BadgeCategory.GOAL_HUNTERS:
        return 'user.badges.categories.goalHunters';
      case BadgeCategory.HALL_OF_FAME:
        return 'user.badges.categories.hallOfFame';
      case BadgeCategory.CHALLENGES:
        return 'user.challenges.title.default';
      default:
        return 'user.badges.categories.other';
    }
  }

  private getTimesWonLabel(timesWon: number): string {
    if (!timesWon || timesWon === 0) {
      return null;
    }

    if (timesWon > 1) {
      return this.localeService.instant('user.badges.timesWon.plural', {
        timesWon: timesWon,
      });
    } else {
      return this.localeService.instant('user.badges.timesWon.singular');
    }
  }

  private shouldShowBadge(badge: BadgeResponse) {
    return this.configService.get().gmfConfig.showRanking || !(badge.id in RankingBadge);
  }

  private isBadgeToClientLees(badge: BadgeResponse) {
    return this.configService.isLees() && badge.id in LeesBadgeNotToShow;
  }

  private getTranslationKey(action: string) {
    switch (action) {
      case 'checkouts':
        return 'user.ranking.points.modal.checkouts';
      case 'ratings':
        return 'user.ranking.points.modal.ratings';
      case 'comments':
        return 'user.ranking.points.modal.comments';
      default:
        return 'user.ranking.points.modal.consumed';
    }
  }
  private getTooltipText(action: string) {
    switch (action) {
      case 'checkouts':
        return 'user.ranking.moreInformation.toBorrow';
      case 'ratings':
        return 'user.ranking.moreInformation.toValue';
      case 'comments':
        return 'user.ranking.moreInformation.toComment';
      default:
        return 'user.ranking.moreInformation.toConsume';
    }
  }
}
